import React, { useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { GripVertical } from 'lucide-react';
import { PROJECT_KANBAN_COLUMNS, PROJECT_TRANSITIONS, projectStatusMeta } from '../../lib/projects';
import ProjectStageTransitionDialog from './ProjectStageTransitionDialog';
import { api } from '../../lib/api';

// ProjectsKanban — drag-drop Kanban view of projects (item 2.3). Uses
// @dnd-kit/core for the drag mechanics. Dropping a card on a column doesn't
// silently transition the project; instead it opens the
// ProjectStageTransitionDialog with the target prefilled, so the structured
// payload always gets captured.

export default function ProjectsKanban({ projects, onChanged }) {
  const navigate = useNavigate();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const [draggingId, setDraggingId] = useState(null);
  const [pending, setPending] = useState(null); // { project, target, forceMode }
  const draggingProject = projects.find((p) => p.id === draggingId);

  const byStatus = {};
  for (const s of PROJECT_KANBAN_COLUMNS) byStatus[s] = [];
  for (const p of projects) if (byStatus[p.status]) byStatus[p.status].push(p);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDraggingId(null);
    if (!over) return;
    const targetStatus = over.id;
    const project = projects.find((p) => p.id === active.id);
    if (!project || project.status === targetStatus) return;
    if (!PROJECT_KANBAN_COLUMNS.includes(targetStatus)) return;
    // If the drop target isn't an allowed transition from the current status
    // (e.g., dragging forward across multiple columns), open the dialog in
    // force mode so the user gets the override-reason flow directly instead
    // of having to click an extra "More options" button.
    const allowed = PROJECT_TRANSITIONS[project.status] || [];
    const forceMode = !allowed.includes(targetStatus);
    setPending({ project, target: targetStatus, forceMode });
  };

  const handleConfirmed = async (payload) => {
    const res = await api.post(`/api/admin/projects/${pending.project.id}/status`, payload);
    if (res?.project) onChanged?.(res.project);
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={(e) => setDraggingId(e.active.id)} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="projects-kanban">
          {PROJECT_KANBAN_COLUMNS.map((status) => (
            <Column key={status} status={status} cards={byStatus[status]} onCardClick={(p) => navigate(`/admin/projects/${p.id}`)} />
          ))}
        </div>
        <DragOverlay>
          {draggingProject ? (
            <div className="rounded-xl border border-[#B0FC38]/40 bg-[#0a0a0f] p-3 w-72 shadow-[0_10px_40px_-10px_rgba(176,252,56,0.5)]">
              <div className="text-[13px] text-white font-medium truncate">{draggingProject.name}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {pending && (
        <ProjectStageTransitionDialog
          open
          project={{ ...pending.project }}
          forceMode={pending.forceMode}
          defaultTarget={pending.target}
          onClose={() => setPending(null)}
          onConfirm={async (payload) => {
            await handleConfirmed(payload);
            setPending(null);
          }}
        />
      )}
    </>
  );
}

function Column({ status, cards, onCardClick }) {
  const meta = projectStatusMeta(status);
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${status}`}
      className={`shrink-0 w-72 rounded-2xl border ${meta.tone.split(' ').filter((c) => c.startsWith('border-')).join(' ')} bg-white/[0.015] flex flex-col ${isOver ? 'ring-1 ring-[#B0FC38]/40' : ''}`}
    >
      <div className={`px-3 py-2 rounded-t-2xl border-b border-white/[0.05] ${meta.tone}`}>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium uppercase tracking-wider">{meta.label}</div>
          <div className="text-[11px] opacity-70">{cards.length}</div>
        </div>
      </div>
      <div className="p-2 space-y-2 min-h-[120px]">
        {cards.length === 0 ? (
          <div className="text-[11px] text-gray-600 text-center py-4">No projects</div>
        ) : (
          cards.map((p) => <Card key={p.id} project={p} onOpen={() => onCardClick?.(p)} />)
        )}
      </div>
    </div>
  );
}

function Card({ project, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: project.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      data-testid={`kanban-card-${project.id}`}
      className={`rounded-xl border border-white/[0.08] bg-[#0a0a0f]/80 p-3 ${isDragging ? 'opacity-30' : ''} cursor-pointer`}
      onClick={onOpen}
    >
      <div className="flex items-start gap-2">
        <button
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5 text-gray-600 hover:text-gray-400 cursor-grab active:cursor-grabbing"
          aria-label="Drag"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-white font-medium truncate">{project.name}</div>
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500">
            <span className="uppercase tracking-wider">{project.type}</span>
            {project.targetDeliveryDate && <span>· target {project.targetDeliveryDate}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
